import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryFetch } from 'sonata-react-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getDealPublisher, getDeals, getSites } from '../../../../services/deals.service';
import { DealsFilters } from './Filters/DealsFilters';

import './dealsSelection.scss';

import { DealsSelectionList } from './List/DealsSelectionList';
import { useDealsBidFloorFilter } from './useDealsBidFloorFilter';

import type { LineItemGoal } from 'sonata-static-storage/lib/entities';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';

type State = {
  advanceFilters: { id: string; value: any }[];
  searchText: string[];
  selectedDeals: string[];
};

type Props = {
  /** Entity goals, used to estimate the max premium CPM of performance (non-CPM) pricing models. */
  goals?: LineItemGoal[];
  /** Entity pricing config: deals whose bid floor exceeds its max premium CPM after fees are hidden. */
  pricingModelConfig?: PricingModelConfig;
};

export function DealsSelection({ goals, pricingModelConfig }: Props) {
  const { formatMessage } = useIntl();
  const token: Record<string, any> = {};

  const { familyType, setDeals, targeting } = useTargetingContext();

  const [{ advanceFilters, searchText, selectedDeals }, setState] = useState<State>({
    advanceFilters: [],
    searchText: [],
    selectedDeals: targeting.deals
  });

  const { data: sites, isLoading: sitesLoading } = useQueryFetch('sites', async () => getSites(token));
  const { data: allDeals, isLoading: dealsLoading } = useQueryFetch('deals', async () => getDeals(token));
  const { deals, isLoading: bidFloorLoading } = useDealsBidFloorFilter(
    allDeals as DealInterface[] | undefined,
    pricingModelConfig,
    goals
  );
  const { data: dealPublishers, isLoading: publishersLoading } = useQueryFetch(
    `dealsPublishers-${allDeals?.map((deal: DealInterface) => deal._id)}`,
    async () => {
      return Promise.all(
        allDeals?.map(async (deal: DealInterface) => {
          if (!deal.publisher) {
            return {
              id: deal._id,
              publisher: 'N/A'
            };
          }
          return {
            id: deal._id,
            publisher: (await getDealPublisher(deal.publisher, token)).name
          };
        })
      );
    },
    {
      enabled: allDeals && allDeals.length > 1
    }
  );

  const handleSelectionChange = (items: Record<string, any>[]) => {
    const visibleIds = (filteredDealsList ?? []).map((deal: DealInterface) => deal._id?.toString());
    const selectedVisibleIds = items.map(item => item.sonataId);
    const newSelectedDeals = [...targeting.deals.filter(id => !visibleIds.includes(id)), ...selectedVisibleIds];
    setState(prevState => ({
      ...prevState,
      selectedDeals: newSelectedDeals
    }));
    setDeals(newSelectedDeals);
  };

  const handleChange = (filters: { advanceFilters: { id: string; value: any }[]; text?: string }) => {
    setState(prevState => ({
      ...prevState,
      advanceFilters: filters.advanceFilters || [],
      ...(filters.text !== undefined && { searchText: filters.text.split(',').map((a: string) => a.trim()) })
    }));
  };

  const handleInputFilterChange = (text: string) => {
    const searchValues = text.length ? text.split(',').map((a: string) => a.trim()) : [];
    setState(prevState => ({
      ...prevState,
      searchText: searchValues
    }));
  };

  const filterDealsList = () => {
    const textFilteredDeals =
      searchText.length > 0
        ? deals?.filter((deal: DealInterface) => {
            return searchText.some(text => {
              let found = false;
              if (deal.origin.name === 'broadsign') {
                found = deal.origin.additionalInfo.external_id === text;
              } else {
                found = deal.origin.id.toString() === text;
              }
              if (!found) {
                found =
                  deal.name.toLowerCase().includes(text.toLowerCase()) ||
                  deal.alias?.toLowerCase().includes(text.toLowerCase());
              }
              return found;
            });
          })
        : deals;

    return advanceFilters?.length
      ? textFilteredDeals?.filter((deal: DealInterface) => {
          return advanceFilters.every((filter: Record<string, any>) => {
            const filterValues = filter.value.map((v: any) => v.id);

            if (filterValues.length === 0) {
              return true;
            }

            if (filter.id === 'country') {
              if (filterValues.includes('N/A')) {
                return !deal.country;
              }
              return filterValues.includes(deal.country);
            }

            if (filter.id === 'appAndSite') {
              if (filterValues.includes('N/A')) {
                return !deal.placements || deal.placements.length === 0;
              }
              return deal.placements.some((placement: any) => {
                const site = sites?.items?.find((s: Record<string, any>) => s._id === placement.site);
                return site && filterValues.includes(site.name);
              });
            }

            if (filter.id === 'publisher') {
              const publisherInfo = dealPublishers?.find(publisher => publisher.id === deal._id);
              return filterValues.includes(publisherInfo?.publisher);
            }

            if (filter.id === 'sourceType') {
              return filterValues.includes(deal.sourceType);
            }

            if (filter.id === 'supplySource') {
              return filterValues.includes(deal.origin.name);
            }

            if (filter.id === 'creativeSize') {
              if (filterValues.includes('N/A')) {
                return !deal.creatives || deal.creatives.length === 0;
              }
              return deal.creatives.some((creative: string) => filterValues.includes(creative));
            }

            return true;
          });
        })
      : textFilteredDeals;
  };

  const filteredDealsList = filterDealsList() ?? [];

  const isLoading = dealsLoading || sitesLoading || publishersLoading || bidFloorLoading;

  const selectedItems = useMemo(() => {
    const selectedDealsSet = new Set(selectedDeals);
    return filteredDealsList?.reduce((acc: Record<string, any>, deal: Record<string, any>, index: number) => {
      if (selectedDealsSet.has(deal._id)) {
        acc[index] = true;
      }
      return acc;
    }, {});
  }, [filteredDealsList, selectedDeals]);

  return (
    <div className="dealSelection">
      <div className="dealSelection__title fontRegular">
        {formatMessage({
          defaultMessage: 'DEAL SELECTION',
          id: 'appAndSiteMarketplace.dealSelection.title'
        })}
        <span>*</span>
      </div>
      <div className="dealSelection__container">
        <DealsFilters
          dealPublishers={dealPublishers}
          deals={deals as DealInterface[]}
          isLoading={isLoading}
          onChange={handleChange}
          onInputFilterChange={handleInputFilterChange}
          sites={sites}
        />
        <DealsSelectionList
          dealPublishers={dealPublishers}
          deals={filteredDealsList}
          isLoading={isLoading}
          onChangeSelection={handleSelectionChange}
          selectedDeals={selectedItems}
          sites={sites}
        />
      </div>
      {familyType !== CampaignFamilyEnum.dooh && (
        <div className="dealSelection__title dealSelection__title--include fontRegular">
          {formatMessage({
            defaultMessage: 'INCLUDE / EXCLUDE INVENTORY',
            id: 'appAndSiteMarketplace.dealSelection.titleIncludeExclude'
          })}
        </div>
      )}
    </div>
  );
}
